// ============================================================================
// Overall Control — POs tab on real data (spec: docs/overall-control/POS_TAB_SPEC.md).
//
// Summary: Total PO raised / PO not raised, Total carts / Pending carts, At risk / Breached,
// Open escalations / Open > 24h. Stuck by stage: Cart, PO raise, Escalations. Delay ownership,
// Billing and Team · by PO owner keep their structure with "–" until their rules are given.
// Breakdown counts POs raised in the range (by destination, origin hub or vendor).
// ============================================================================
import { Alert, Segmented, Skeleton, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { getPosOverview } from "../../api/orders";
import { fmtWeight } from "../../format";
import { OcPosBreakdownRow, OcPosDimension, OcRow, OcScopeParams } from "../../types/orders";
import MetricLink from "../metric_link";
import OrdersListDrawer, { OrdersListRequest } from "../orders/orders_list_drawer";
import {
  AtRiskCell,
  BreachedCell,
  Cell,
  DASH,
  EscalationTeamsTable,
  OpenEscalationsCell,
  Section,
  StagesTable,
  TeamTable,
  TechErrorsCell,
  useTabOverview,
  whenLabel,
} from "../real/common";
import { BillingStructure, DashTable, PO_OWNER_COLUMNS } from "../tab_structure";
import styles from "../../index.module.scss";

// Delay-ownership rows for the POs tab (SH8): one per stage group.
const OWNERSHIP_ROWS = [
  { team: "Cart", stages: "Availability check (lead time)" },
  { team: "PO raise", stages: "PO creation in Zoho" },
];

interface Props {
  scope: OcScopeParams;
  rangeLabel: string;
  onStatus: (s: { loading: boolean; updatedAt: Date | null; refresh: () => void }) => void;
  onOpenPath: (r: OcRow) => void;
}

const PosTab: React.FC<Props> = ({ scope, rangeLabel, onStatus, onOpenPath }) => {
  const { data, error, loading, updatedAt, refresh } = useTabOverview(getPosOverview, scope, "POs");
  const [drawer, setDrawer] = useState<OrdersListRequest | null>(null);
  const [dimension, setDimension] = useState<OcPosDimension>("destination");

  useEffect(() => onStatus({ loading, updatedAt, refresh }), [loading, updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (title: string, metric: string) => setDrawer({ mode: "metric", title, metric, scope, tab: "pos" });
  const drill = (title: string, metric: string) => () => open(title, metric);
  const when = whenLabel(rangeLabel);
  // Carts have no order or estimate, so they have no EC: the cart blocks ignore the EC filter.
  const ecNote = scope.branches?.length ? " · all ECs (carts have no EC)" : "";
  // Same note on the cart-line counts inside At risk / Breached (they ignore the EC too).
  const cartNote = scope.branches?.length ? " (all ECs)" : undefined;

  if (error && !data) return <Alert type="error" showIcon message={error} className={styles.inlineAlert} />;
  if (!data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const s = data.summary;

  return (
    <div>
      {error ? <Alert type="warning" showIcon message={`Showing the last loaded numbers: ${error}`} className={styles.inlineAlert} /> : null}

      <div className={styles.panel}>
        <div className={styles.summaryGrid}>
          {/* Row 1 */}
          <Cell
            tone="brand"
            label={`Total PO raised ${when}`}
            sub={
              <>
                {fmtWeight(s.po_raised.weight_kg)}
                {" · "}
                <MetricLink
                  value={`${s.po_raised.cancelled} cancelled`}
                  count={s.po_raised.cancelled}
                  onClick={drill(`POs raised ${when} · cancelled since`, "po_raised_cancelled")}
                />
                {" · "}
                <MetricLink
                  value={`${s.po_raised.after_6pm} after 6 PM`}
                  count={s.po_raised.after_6pm}
                  title="Reached Zoho at or after 6 PM (IST) on the day it was raised"
                  onClick={drill(`POs raised ${when} · after 6 PM`, "po_raised_after_6pm")}
                />
              </>
            }
          >
            <MetricLink value={s.po_raised.count} count={s.po_raised.count} onClick={drill(`POs raised ${when}`, "po_raised")} />
          </Cell>
          <Cell tone="brand" label={`Total carts ${when}`} sub={`cart lines with lead time updated${ecNote}`}>
            <MetricLink value={s.carts.count} count={s.carts.count} onClick={drill(`Cart lines with lead time updated ${when}`, "carts")} />
          </Cell>
          <AtRiskCell v={s.at_risk} onDrill={open} cartNote={cartNote} />
          <OpenEscalationsCell esc={s.escalations} onDrill={open} />

          {/* Row 2 */}
          <Cell
            tone="warn"
            label="PO not raised yet"
            sub={
              <MetricLink
                value={`+${s.po_not_raised.older} older`}
                count={s.po_not_raised.older}
                onClick={drill("PO not raised · older orders", "po_not_raised_older")}
              />
            }
          >
            <MetricLink value={s.po_not_raised.count} count={s.po_not_raised.count} onClick={drill(`PO not raised ${when}`, "po_not_raised")} />
          </Cell>
          <Cell
            tone="warn"
            label="Pending carts"
            sub={
              <>
                <MetricLink
                  value={`+${s.pending_carts.older} older`}
                  count={s.pending_carts.older}
                  onClick={drill("Pending cart lines · asked earlier", "pending_carts_older")}
                />
                {` · ${s.pending_carts.pending_tab} in Pending tab${ecNote}`}
              </>
            }
          >
            <MetricLink value={s.pending_carts.count} count={s.pending_carts.count} onClick={drill(`Pending cart lines ${when}`, "pending_carts")} />
          </Cell>
          <BreachedCell v={s.breached} onDrill={open} cartNote={cartNote} />
          <TechErrorsCell tech={s.tech_errors} onDrill={open} />
        </div>
      </div>

      <Section title="Stuck by stage">
        <StagesTable stages={data.stages} onDrill={open} />
      </Section>

      <Section title="Delay ownership">
        <div className={styles.sectionNote}>Delayed, TAT and the rest arrive with the at-risk and breach rules.</div>
        <TeamTable teams={OWNERSHIP_ROWS} first="Owner" columns={["Delayed", "TAT", "Weight", "Avg slip"]} />
        <h4 className={styles.subhead}>Escalations · by the team that resolves them</h4>
        <EscalationTeamsTable rows={data.escalation_teams} onDrill={open} />
      </Section>

      <BillingStructure />

      <Section title="Breakdown">
        <PosBreakdownTable dimension={dimension} onDimension={setDimension} rows={data.breakdown[dimension]} onDrill={open} />
      </Section>

      <Section title="Team · by PO owner">
        <div className={styles.sectionNote}>PO owner logic is to be defined; the numbers appear once it is.</div>
        <DashTable columns={PO_OWNER_COLUMNS} />
      </Section>

      <OrdersListDrawer request={drawer} onClose={() => setDrawer(null)} onOpenPath={onOpenPath} />
    </div>
  );
};

const DIMENSIONS: { label: string; value: OcPosDimension }[] = [
  { label: "Destination", value: "destination" },
  { label: "Origin hub", value: "origin_hub" },
  { label: "Vendor", value: "vendor" },
];

const PosBreakdownTable: React.FC<{
  dimension: OcPosDimension;
  onDimension: (d: OcPosDimension) => void;
  rows: OcPosBreakdownRow[];
  onDrill: (title: string, metric: string) => void;
}> = ({ dimension, onDimension, rows, onDrill }) => {
  const m = (part: string, label: string) => (v: number, r: OcPosBreakdownRow) =>
    <MetricLink value={v} count={v} onClick={() => onDrill(`${r.label} · ${label}`, `bd:${dimension}:${r.key}:${part}`)} />;
  const pending = { align: "right" as const, width: 90, render: () => DASH };
  const columns: ColumnsType<OcPosBreakdownRow> = [
    {
      title: DIMENSIONS.find((d) => d.value === dimension)?.label,
      dataIndex: "label",
      ellipsis: true,
      render: (v: string) => <span className={styles.strong}>{v}</span>,
    },
    { title: "POs", dataIndex: "total", align: "right", width: 80, render: m("total", "POs") },
    { title: "Weight", dataIndex: "weight_kg", align: "right", width: 90, render: (v: number) => fmtWeight(v) },
    { title: "GRN done %", dataIndex: "grn_done_pct", align: "right", width: 100, render: (v: number | null) => (v == null ? DASH : `${v}%`) },
    { title: "GRN done", dataIndex: "grn_done", align: "right", width: 90, render: m("grn_done", "GRN done") },
    { title: "Waiting at GRN", dataIndex: "grn_waiting", align: "right", width: 120, render: m("grn_waiting", "waiting at GRN") },
    { title: "At risk", key: "at_risk", ...pending },
    { title: "Breached", key: "breached", ...pending },
    { title: "Open esc.", key: "open_escalations", ...pending },
  ];
  return (
    <div>
      <div className={styles.sectionToolbar}>
        <Segmented size="small" value={dimension} onChange={(v) => onDimension(v as OcPosDimension)} options={DIMENSIONS} />
        <span className={styles.muted}>POs raised in the range.</span>
      </div>
      <Table<OcPosBreakdownRow>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
        className={styles.denseTable}
        scroll={{ x: 1000 }}
        locale={{ emptyText: "No POs raised in this range." }}
      />
    </div>
  );
};

export default PosTab;
